import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import {
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import useAuthentication from "../../hooks/useAuthentication";
import usePrint from "../../hooks/usePrint";
import usePopup from "../../hooks/usePopup";
import { base, baseURL } from "../../constants";
import { isEmrRoute } from "../../utilis/methods/voiceAssistant/emrRoute";
import {
  VOICE_COMMAND_EVENT,
  dispatchVoiceListeningCycleEnd,
  type VoiceCommandDetail,
} from "../../context/VoiceAssistant/VoiceAssistantContext";
import { voicePromptService } from "../../services/voiceAssistantService";
import type { ResponseType } from "../../Types/ServicesType/servicesType";
import type {
  FieldSummaryData,
  PatientSelectionItem,
  VoicePromptRequest,
  VoicePromptResponseBody,
  VoicePromptType,
} from "../../Types/VoiceAssistant/VoiceAssistantTypes";
import FieldSummaryModal from "./FieldSummaryModal";
import PatientSelectionDialog from "./PatientSelectionDialog";
import { inferVoicePromptType } from "./inferVoicePromptType";
import {
  dispatchAiChatUiVoiceCommand,
  dispatchVoiceChatInput,
  parseAiChatVoiceCommand,
  VOICE_CHAT_MODE_EVENT,
} from "../../utilis/methods/voiceAssistant/aiChatVoiceCommands";
import { buildEmrEncounterUrl } from "../../utilis/methods/voiceAssistant/emrEncounterPath";
import { isEmrLandingRoute } from "../../utilis/methods/voiceAssistant/emrLandingRoute";
import {
  getPatientSelectionNavigateTarget,
  normalizePatientSelectionData,
} from "../../utilis/methods/voiceAssistant/patientSelectionVoice";
import {
  VOICE_AI_RESPONSE_EVENT,
  type VoiceAiResponseDetail,
} from "./voiceAssistantEvents";
import {
  downloadBase64File,
  isPatientServiceFileResponse,
  resolvePatientServiceFile,
} from "../../utilis/methods/voiceAssistant/resolvePatientServiceFile";

function navigateToMenuUrl(
  navigate: ReturnType<typeof useNavigate>,
  menuUrl: string,
) {
  const path = menuUrl.trim().replace(/^\//, "");
  if (!path) return;
  if (path.includes("ui")) {
    navigate(`${base}${path}`);
  } else {
    window.location.href = `${baseURL}angular/#${path}`;
  }
}

type ParsedVoiceApiResult = {
  type: VoicePromptType;
  declaredFileType: string | null;
  data: VoicePromptResponseBody["data"] | null;
};

function parseVoiceApiResult(
  result: ResponseType<VoicePromptResponseBody>,
  requestType: VoicePromptType,
): ParsedVoiceApiResult {
  const body = result.response;

  if (!body) {
    return { type: requestType, declaredFileType: null, data: null };
  }

  return {
    type: normalizeResponseType(body.type) ?? requestType,
    declaredFileType: body.type ?? null,
    data: body.data ?? null,
  };
}

function normalizeResponseType(type: string | null | undefined): VoicePromptType {
  if (!type) return null;
  if (type === "field summary" || type === "field-summary") return "field-summary";
  if (type === "patient selection" || type === "patient-selection") {
    return "patient-selection";
  }
  if (type === "menu" || type === "pdf" || type === "translate" || type === "chat") {
    return type;
  }
  return null;
}

function dispatchEmrAiResponse(detail: VoiceAiResponseDetail) {
  window.dispatchEvent(
    new CustomEvent<VoiceAiResponseDetail>(VOICE_AI_RESPONSE_EVENT, { detail }),
  );
}

export default function VoiceCommandHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthentication();
  const { printPdf } = usePrint();
  const { showSnackbar } = usePopup();
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryData, setSummaryData] = useState<FieldSummaryData | null>(null);
  const [patientSelectionOpen, setPatientSelectionOpen] = useState(false);
  const [patientSelectionList, setPatientSelectionList] = useState<
    PatientSelectionItem[]
  >([]);
  const pendingQueueRef = useRef<string[]>([]);
  const isProcessingRef = useRef(false);
  const processNextRef = useRef<() => void>(() => {});
  const voiceChatActiveRef = useRef(false);

  const finishRequest = useCallback(() => {
    isProcessingRef.current = false;
    dispatchVoiceListeningCycleEnd();
    processNextRef.current();
  }, []);

  const openEmrPatient = useCallback(
    (patient: PatientSelectionItem) => {
      const target = getPatientSelectionNavigateTarget(patient);
      if (!target) {
        showSnackbar({
          open: true,
          message: "Patient record is missing encounter or patient id.",
          variant: "warning",
          duration: 5000,
        });
        return;
      }
      navigate(
        buildEmrEncounterUrl(
          target.encounterId,
          target.patientMasterId,
          target.emrNo,
        ),
      );
      setPatientSelectionOpen(false);
      setPatientSelectionList([]);
    },
    [navigate, showSnackbar],
  );

  const { mutate: submitVoicePrompt, isLoading } = useMutation(voicePromptService, {
    onSuccess: (result, variables) => {
      if (result.responseIndicator !== "success") {
        showSnackbar({
          open: true,
          message: result.responseMessage || "Voice command could not be processed.",
          variant: "warning",
          duration: 5000,
        });
        finishRequest();
        return;
      }

      const { type, data, declaredFileType } = parseVoiceApiResult(
        result,
        variables.type,
      );
      const onEmr = isEmrRoute(location.pathname);

      if (onEmr) {
        if (
          type === "chat" ||
          type === "pdf" ||
          isPatientServiceFileResponse(type, declaredFileType)
        ) {
          dispatchEmrAiResponse({
            type: type === "chat" ? "chat" : "pdf",
            data,
            declaredFileType,
          });
          window.dispatchEvent(new CustomEvent("rcm-ai-drawer-open"));
        }
        finishRequest();
        return;
      }

      switch (type) {
        case "menu": {
          const menuUrl = typeof data === "string" ? data : "";
          if (menuUrl) {
            navigateToMenuUrl(navigate, menuUrl);
          } else {
            showSnackbar({
              open: true,
              message: "Menu navigation URL was not returned.",
              variant: "warning",
              duration: 5000,
            });
          }
          break;
        }
        case "pdf": {
          if (
            typeof data === "string" &&
            data &&
            isPatientServiceFileResponse(type, declaredFileType)
          ) {
            const fileType = resolvePatientServiceFile(declaredFileType, data);
            if (fileType === "pdf") {
              printPdf(data, "new-tab");
            } else if (fileType === "xlsx" || fileType === "xls") {
              downloadBase64File(data, fileType);
            } else if (fileType === "image") {
              printPdf(data, "new-tab", "png");
            } else {
              showSnackbar({
                open: true,
                message: "Unsupported file type returned by patient service.",
                variant: "warning",
                duration: 5000,
              });
            }
          }
          break;
        }
        case "translate": {
          if (typeof data === "string" && data) {
            showSnackbar({
              open: true,
              message: data,
              variant: "info",
              duration: 8000,
              origin: { vertical: "top", horizontal: "right" },
            });
          }
          break;
        }
        case "field-summary": {
          if (data && typeof data === "object" && !Array.isArray(data)) {
            setSummaryData(data as FieldSummaryData);
            setSummaryOpen(true);
          }
          break;
        }
        case "patient-selection": {
          if (!isEmrLandingRoute(location.pathname)) {
            showSnackbar({
              open: true,
              message: "Patient selection by voice is only available on the EMR listing page.",
              variant: "info",
              duration: 5000,
            });
            break;
          }
          const patients = normalizePatientSelectionData(data);
          if (patients.length === 0) {
            showSnackbar({
              open: true,
              message: "No matching patients were found.",
              variant: "warning",
              duration: 5000,
            });
            break;
          }
          if (patients.length === 1) {
            openEmrPatient(patients[0]);
            break;
          }
          setPatientSelectionList(patients);
          setPatientSelectionOpen(true);
          break;
        }
        default: {
          if (typeof data === "string" && data) {
            showSnackbar({
              open: true,
              message: data,
              variant: "info",
              duration: 5000,
            });
          }
          break;
        }
      }

      finishRequest();
    },
    onError: (error: { message?: string; response?: { data?: { responseMessage?: string } } }) => {
      showSnackbar({
        open: true,
        message:
          error?.response?.data?.responseMessage ||
          error?.message ||
          "Voice command request failed.",
        variant: "error",
        duration: 5000,
      });
      finishRequest();
    },
  });

  const buildPayload = useCallback(
    (prompt: string): VoicePromptRequest => ({
      date: dayjs().format("YYYY-MM-DD"),
      prompt,
      type: inferVoicePromptType(prompt, location.pathname),
      language: navigator.language || null,
      userId: user?.userId ?? user?.login_id ?? null,
    }),
    [location.pathname, user?.login_id, user?.userId],
  );

  const processNext = useCallback(() => {
    if (isProcessingRef.current || pendingQueueRef.current.length === 0) return;
    const nextPrompt = pendingQueueRef.current.shift();
    if (!nextPrompt) return;
    isProcessingRef.current = true;
    submitVoicePrompt(buildPayload(nextPrompt));
  }, [buildPayload, submitVoicePrompt]);

  processNextRef.current = processNext;

  const enqueuePrompt = useCallback(
    (prompt: string) => {
      pendingQueueRef.current.push(prompt);
      processNext();
    },
    [processNext],
  );

  useEffect(() => {
    const onVoiceChatMode = (event: Event) => {
      voiceChatActiveRef.current = Boolean(
        (event as CustomEvent<{ active: boolean }>).detail?.active,
      );
    };

    window.addEventListener(VOICE_CHAT_MODE_EVENT, onVoiceChatMode);
    return () => window.removeEventListener(VOICE_CHAT_MODE_EVENT, onVoiceChatMode);
  }, []);

  useEffect(() => {
    const onVoiceCommand = (event: Event) => {
      const { text } = (event as CustomEvent<VoiceCommandDetail>).detail;
      const trimmed = text?.trim();
      if (!trimmed) return;

      const uiAction = parseAiChatVoiceCommand(trimmed);
      if (uiAction) {
        dispatchAiChatUiVoiceCommand(uiAction);
        dispatchVoiceListeningCycleEnd();
        return;
      }

      if (voiceChatActiveRef.current) {
        dispatchVoiceChatInput(trimmed);
        return;
      }

      if (isEmrRoute(location.pathname)) return;
      enqueuePrompt(trimmed);
    };

    window.addEventListener(VOICE_COMMAND_EVENT, onVoiceCommand);
    return () => window.removeEventListener(VOICE_COMMAND_EVENT, onVoiceCommand);
  }, [enqueuePrompt, location.pathname]);

  if (isEmrRoute(location.pathname)) {
    return null;
  }

  return (
    <>
      {isLoading ? (
        <Box
          sx={{
            position: "fixed",
            bottom: 96,
            right: 88,
            zIndex: (theme) => theme.zIndex.snackbar + 3,
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: "background.paper",
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <CircularProgress size={18} />
          <Typography variant="caption">Processing voice command…</Typography>
        </Box>
      ) : null}
      <FieldSummaryModal
        open={summaryOpen}
        data={summaryData}
        onClose={() => {
          setSummaryOpen(false);
          setSummaryData(null);
        }}
      />
      <PatientSelectionDialog
        open={patientSelectionOpen}
        patients={patientSelectionList}
        onSelect={openEmrPatient}
        onClose={() => {
          setPatientSelectionOpen(false);
          setPatientSelectionList([]);
        }}
      />
    </>
  );
}
